import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { 
  PenTool, 
  FileText, 
  Send, 
  Clock, 
  CheckCircle2, 
  XCircle,
  Eye,
  Download,
  Plus,
  Settings,
  ExternalLink,
  AlertTriangle,
  Loader2,
  Users,
  Calendar,
  Link as LinkIcon,
  RefreshCcw,
  Trash2,
  Mail
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { PageLoader } from '../components/common/LoadingSpinner';

const STATUS_COLORS = {
  created: 'bg-slate-100 text-slate-700',
  sent: 'bg-blue-100 text-blue-700',
  delivered: 'bg-blue-100 text-blue-700',
  viewed: 'bg-yellow-100 text-yellow-700',
  signed: 'bg-green-100 text-green-700',
  completed: 'bg-green-100 text-green-700',
  declined: 'bg-red-100 text-red-700',
  voided: 'bg-slate-100 text-slate-700'
};

const STATUS_ICONS = {
  created: FileText,
  sent: Send,
  delivered: Mail,
  viewed: Eye,
  signed: PenTool,
  completed: CheckCircle2,
  declined: XCircle,
  voided: Trash2
};

export const ESignature = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [clients, setClients] = useState([]);
  const [cases, setCases] = useState([]);
  const [isConfigured, setIsConfigured] = useState(false);
  const [showNewRequest, setShowNewRequest] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [newRequest, setNewRequest] = useState({
    document_id: '',
    document_name: '',
    case_id: '',
    recipients: [{ name: '', email: '', role: 'signer', signing_order: 1 }],
    subject: '',
    message: ''
  });
  
  // DocuSign credentials state
  const [docusignCredentials, setDocusignCredentials] = useState({
    client_id: '',
    client_secret: ''
  });
  const [savingCredentials, setSavingCredentials] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [requestsRes, docsRes, clientsRes, casesRes] = await Promise.all([
        api.get('/esignature/requests').catch(() => ({ data: [] })),
        api.get('/documents?limit=50').catch(() => ({ data: [] })),
        api.get('/clients?limit=100').catch(() => ({ data: [] })),
        api.get('/cases?limit=100').catch(() => ({ data: [] }))
      ]);
      
      setRequests(requestsRes.data || []);
      setDocuments(docsRes.data || []);
      setClients(clientsRes.data || []);
      setCases(casesRes.data || []);
      
      // Check if DocuSign is configured (mock check - in real impl, check env vars)
      // For now, we'll show the interface regardless
      setIsConfigured(false); // Set to false to show setup instructions
    } catch (error) {
      console.error('Failed to fetch e-signature data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRecipient = () => {
    setNewRequest(prev => ({
      ...prev,
      recipients: [
        ...prev.recipients,
        { name: '', email: '', role: 'signer', signing_order: prev.recipients.length + 1 }
      ]
    }));
  };

  const handleRemoveRecipient = (index) => {
    setNewRequest(prev => ({
      ...prev,
      recipients: prev.recipients.filter((_, i) => i !== index)
    }));
  };

  const handleRecipientChange = (index, field, value) => {
    setNewRequest(prev => ({
      ...prev,
      recipients: prev.recipients.map((r, i) => 
        i === index ? { ...r, [field]: value } : r
      )
    }));
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const response = await api.post('/esignature/request', {
        document_id: newRequest.document_id,
        case_id: newRequest.case_id || null,
        recipients: newRequest.recipients,
        subject: newRequest.subject,
        message: newRequest.message
      });
      
      setRequests(prev => [response.data, ...prev]);
      setShowNewRequest(false);
      setNewRequest({
        document_id: '',
        document_name: '',
        case_id: '',
        recipients: [{ name: '', email: '', role: 'signer', signing_order: 1 }],
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Failed to create signature request:', error);
      alert(error.response?.data?.detail || 'Failed to create signature request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVoidRequest = async (requestId) => {
    if (!confirm('Are you sure you want to void this signature request?')) return;
    
    try {
      await api.delete(`/esignature/requests/${requestId}`);
      setRequests(prev => prev.filter(r => r.request_id !== requestId));
    } catch (error) {
      console.error('Failed to void request:', error);
    }
  };

  const handleSaveDocuSignCredentials = async () => {
    if (!docusignCredentials.client_id || !docusignCredentials.client_secret) {
      toast.error('Please enter both Client ID and Secret Key');
      return;
    }
    
    setSavingCredentials(true);
    try {
      await api.put('/settings/integrations', {
        docusign_client_id: docusignCredentials.client_id,
        docusign_client_secret: docusignCredentials.client_secret
      });
      toast.success('DocuSign credentials saved successfully');
      setShowSetup(false);
      setIsConfigured(true);
    } catch (error) {
      console.error('Failed to save DocuSign credentials:', error);
      toast.error(error.response?.data?.detail || 'Failed to save credentials');
    } finally {
      setSavingCredentials(false);
    }
  };

  const getStatusIcon = (status) => {
    const Icon = STATUS_ICONS[status] || FileText;
    return <Icon className="h-4 w-4" />;
  };

  if (loading) {
    return <PageLoader text="Loading e-signature..." />;
  }

  // Stats
  const stats = {
    total: requests.length,
    pending: requests.filter(r => ['created', 'sent', 'delivered', 'viewed'].includes(r.status)).length,
    completed: requests.filter(r => r.status === 'completed').length,
    declined: requests.filter(r => r.status === 'declined').length
  };

  return (
    <div className="space-y-6" data-testid="esignature-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">E-Signatures</h1>
          <p className="text-slate-500 mt-1">
            Send documents for electronic signature using DocuSign
          </p>
        </div>
        <div className="flex gap-2">
          <Dialog open={showSetup} onOpenChange={setShowSetup}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm">
                <Settings className="h-4 w-4 mr-2" />
                Setup
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>DocuSign Integration Setup</DialogTitle>
                <DialogDescription>
                  Configure your DocuSign credentials to enable e-signatures
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>API Credentials Required</AlertTitle>
                  <AlertDescription>
                    To use real e-signatures, you need DocuSign API credentials.
                  </AlertDescription>
                </Alert>
                
                <div className="space-y-4">
                  <h4 className="font-medium">How to get DocuSign credentials:</h4>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-slate-600">
                    <li>Go to <a href="https://developers.docusign.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">developers.docusign.com</a></li>
                    <li>Create a free developer account</li>
                    <li>Create a new app in Apps and Keys</li>
                    <li>Copy your Integration Key (Client ID)</li>
                    <li>Generate a Secret Key</li>
                    <li>Add redirect URI: <code className="text-xs bg-slate-100 px-1 rounded">{window.location.origin}/api/docusign/callback</code></li>
                  </ol>
                  
                  <div className="space-y-3 pt-4">
                    <div className="space-y-2">
                      <Label htmlFor="docusign_client_id">DocuSign Integration Key (Client ID)</Label>
                      <Input 
                        id="docusign_client_id"
                        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" 
                        value={docusignCredentials.client_id}
                        onChange={(e) => setDocusignCredentials(prev => ({ ...prev, client_id: e.target.value }))}
                        data-testid="docusign-client-id-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="docusign_secret">DocuSign Secret Key</Label>
                      <Input 
                        id="docusign_secret"
                        type="password" 
                        placeholder="••••••••••••••••" 
                        value={docusignCredentials.client_secret}
                        onChange={(e) => setDocusignCredentials(prev => ({ ...prev, client_secret: e.target.value }))}
                        data-testid="docusign-secret-input"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowSetup(false)}>Cancel</Button>
                <Button 
                  onClick={handleSaveDocuSignCredentials} 
                  disabled={savingCredentials}
                  data-testid="save-docusign-btn"
                >
                  {savingCredentials ? 'Saving...' : 'Save Credentials'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          
          <Dialog open={showNewRequest} onOpenChange={setShowNewRequest}>
            <DialogTrigger asChild>
              <Button data-testid="new-signature-request-btn">
                <Plus className="h-4 w-4 mr-2" />
                New Signature Request
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Send Document for Signature</DialogTitle>
                <DialogDescription>
                  Select a document and add recipients to send for e-signature
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleSubmitRequest} className="space-y-6 py-4">
                {/* Document Selection */}
                <div className="space-y-2">
                  <Label>Select Document *</Label>
                  <Select 
                    value={newRequest.document_id}
                    onValueChange={(value) => {
                      const doc = documents.find(d => d.document_id === value);
                      setNewRequest(prev => ({
                        ...prev,
                        document_id: value,
                        document_name: doc?.name || '',
                        subject: `Please sign: ${doc?.name || ''}`
                      }));
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a document" />
                    </SelectTrigger>
                    <SelectContent>
                      {documents.map((doc) => (
                        <SelectItem key={doc.document_id} value={doc.document_id}>
                          {doc.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Case Link (Optional) */}
                <div className="space-y-2">
                  <Label>Link to Case (Optional)</Label>
                  <Select 
                    value={newRequest.case_id || 'none'}
                    onValueChange={(value) => setNewRequest(prev => ({ ...prev, case_id: value === 'none' ? '' : value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a case" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No case</SelectItem>
                      {cases.map((c) => (
                        <SelectItem key={c.case_id} value={c.case_id}>
                          {c.title} ({c.case_number})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Recipients */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Recipients *</Label>
                    <Button type="button" variant="outline" size="sm" onClick={handleAddRecipient}>
                      <Plus className="h-3 w-3 mr-1" />
                      Add Recipient
                    </Button>
                  </div>
                  
                  {newRequest.recipients.map((recipient, index) => (
                    <div key={index} className="p-4 border rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Recipient {index + 1}</span>
                        {newRequest.recipients.length > 1 && (
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleRemoveRecipient(index)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Name</Label>
                          <Input
                            placeholder="John Doe"
                            value={recipient.name}
                            onChange={(e) => handleRecipientChange(index, 'name', e.target.value)}
                            required
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Email</Label>
                          <Input
                            type="email"
                            placeholder="john@example.com"
                            value={recipient.email}
                            onChange={(e) => handleRecipientChange(index, 'email', e.target.value)}
                            required
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Role</Label>
                          <Select
                            value={recipient.role}
                            onValueChange={(value) => handleRecipientChange(index, 'role', value)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="signer">Signer</SelectItem>
                              <SelectItem value="cc">CC (Copy)</SelectItem>
                              <SelectItem value="witness">Witness</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Signing Order</Label>
                          <Input
                            type="number"
                            min="1"
                            value={recipient.signing_order}
                            onChange={(e) => handleRecipientChange(index, 'signing_order', parseInt(e.target.value))}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Email Subject */}
                <div className="space-y-2">
                  <Label>Email Subject *</Label>
                  <Input
                    placeholder="Please sign: Document Name"
                    value={newRequest.subject}
                    onChange={(e) => setNewRequest(prev => ({ ...prev, subject: e.target.value }))}
                    required
                  />
                </div>

                {/* Message */}
                <div className="space-y-2">
                  <Label>Message to Recipients</Label>
                  <Textarea
                    placeholder="Please review and sign this document at your earliest convenience."
                    value={newRequest.message}
                    onChange={(e) => setNewRequest(prev => ({ ...prev, message: e.target.value }))}
                    rows={3}
                  />
                </div>

                {!isConfigured && (
                  <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>Demo Mode</AlertTitle>
                    <AlertDescription>
                      DocuSign is not configured. Signature requests will be created locally but not sent.
                      Configure DocuSign in Settings to enable real e-signatures.
                    </AlertDescription>
                  </Alert>
                )}

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setShowNewRequest(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting || !newRequest.document_id}>
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Send for Signature
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Setup Alert */}
      {!isConfigured && (
        <Alert className="bg-amber-50 border-amber-200">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-800">DocuSign Not Configured</AlertTitle>
          <AlertDescription className="text-amber-700">
            E-signatures are in demo mode. Click Setup to configure DocuSign credentials for real electronic signatures.
            <Button variant="link" className="p-0 h-auto ml-2 text-amber-700" onClick={() => setShowSetup(true)}>
              Configure now →
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <FileText className="h-5 w-5 text-slate-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Requests</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Clock className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Pending</p>
                <p className="text-2xl font-bold text-blue-600">{stats.pending}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Completed</p>
                <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Declined</p>
                <p className="text-2xl font-bold text-red-600">{stats.declined}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Requests List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Signature Requests</CardTitle>
            <Button variant="ghost" size="sm" onClick={fetchData}>
              <RefreshCcw className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="all">
            <TabsList>
              <TabsTrigger value="all">All ({requests.length})</TabsTrigger>
              <TabsTrigger value="pending">Pending ({stats.pending})</TabsTrigger>
              <TabsTrigger value="completed">Completed ({stats.completed})</TabsTrigger>
            </TabsList>

            <TabsContent value="all" className="mt-4">
              {requests.length > 0 ? (
                <div className="space-y-3">
                  {requests.map((request) => (
                    <RequestCard 
                      key={request.request_id} 
                      request={request} 
                      onVoid={handleVoidRequest}
                      getStatusIcon={getStatusIcon}
                    />
                  ))}
                </div>
              ) : (
                <EmptyState />
              )}
            </TabsContent>

            <TabsContent value="pending" className="mt-4">
              {requests.filter(r => ['created', 'sent', 'delivered', 'viewed'].includes(r.status)).length > 0 ? (
                <div className="space-y-3">
                  {requests
                    .filter(r => ['created', 'sent', 'delivered', 'viewed'].includes(r.status))
                    .map((request) => (
                      <RequestCard 
                        key={request.request_id} 
                        request={request}
                        onVoid={handleVoidRequest}
                        getStatusIcon={getStatusIcon}
                      />
                    ))}
                </div>
              ) : (
                <p className="text-center text-slate-500 py-8">No pending requests</p>
              )}
            </TabsContent>

            <TabsContent value="completed" className="mt-4">
              {requests.filter(r => r.status === 'completed').length > 0 ? (
                <div className="space-y-3">
                  {requests
                    .filter(r => r.status === 'completed')
                    .map((request) => (
                      <RequestCard 
                        key={request.request_id} 
                        request={request}
                        onVoid={handleVoidRequest}
                        getStatusIcon={getStatusIcon}
                      />
                    ))}
                </div>
              ) : (
                <p className="text-center text-slate-500 py-8">No completed requests</p>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

const RequestCard = ({ request, onVoid, getStatusIcon }) => (
  <div className="p-4 border rounded-lg hover:bg-slate-50 transition-colors">
    <div className="flex items-start justify-between">
      <div className="flex items-start gap-3">
        <div className="p-2 bg-slate-100 rounded-lg">
          <FileText className="h-5 w-5 text-slate-600" />
        </div>
        <div>
          <p className="font-medium">{request.document_name || 'Document'}</p>
          <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
            <Users className="h-3 w-3" />
            <span>{request.recipients?.length || 0} recipient(s)</span>
            <span>•</span>
            <Calendar className="h-3 w-3" />
            <span>{new Date(request.created_at).toLocaleDateString()}</span>
          </div>
          {request.recipients && (
            <div className="mt-2 flex flex-wrap gap-1">
              {request.recipients.map((r, i) => (
                <Badge key={i} variant="outline" className="text-xs">
                  {r.name || r.email}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Badge className={STATUS_COLORS[request.status] || STATUS_COLORS.created}>
          {getStatusIcon(request.status)}
          <span className="ml-1 capitalize">{request.status}</span>
        </Badge>
        {request.status === 'completed' && (
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4" />
          </Button>
        )}
        {['created', 'sent'].includes(request.status) && (
          <Button variant="ghost" size="sm" onClick={() => onVoid(request.request_id)}>
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        )}
      </div>
    </div>
  </div>
);

const EmptyState = () => (
  <div className="text-center py-12">
    <PenTool className="h-16 w-16 text-slate-300 mx-auto mb-4" />
    <h3 className="text-lg font-medium text-slate-900">No Signature Requests</h3>
    <p className="text-slate-500 mt-2">
      Send your first document for electronic signature
    </p>
  </div>
);

export default ESignature;
